"use client";
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FullScreenLoader from "../components/FullScreenLoader";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/24/solid";
import Alert from "../components/Alert";

import CryptoJS from "crypto-js";
const REGISTER_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/register"; // <-- change to your API

const CLIENT_SHARED_SECRET = process.env?.REACT_APP_JWT_SECRET || "122Codeman!";

export default function RegisterNew() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const plan = state?.plan;

  const [alert, setAlert] = useState({
    open: false,
    variant: "info",
    title: "",
    message: "",
    primaryAction: null,
    secondaryAction: null,
  });

  // form state
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ui state
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const handleRegister = async () => {
    setError("");

    // quick client-side checks
    if (!fullName || !phone || !email || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      const clearBody = {
        fullName: fullName.trim(),
        phone: phone.trim(),
        planOption: option,
        email: email,
        password: CryptoJS.AES.encrypt(
          password.trim(),
          CLIENT_SHARED_SECRET,
        ).toString(), // still hash on server after decrypt
      };
      console.log("Registering with body:", clearBody);

      return;

      //console.log(clearBody);
      const res = await fetch(REGISTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(clearBody),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        setOpen(true);
        localStorage.setItem("bv.registerResponse", JSON.stringify(data));
        navigate("/dashboard/assessments");
      } else {
        setAlert({
          open: true,
          variant: "error",
          title: "Registration failed",
          message: "Email already in use. Please try a different one.",
        });
      }
    } catch (e) {
      setError(e.message || "Failed to register.");
    } finally {
      setLoading(false);
    }
  };

  const [option, setoption] = useState("Core");

  return (
    <div className="w-full">
      {loading && <FullScreenLoader />}

      <Alert
        open={alert.open}
        onClose={() => setAlert((a) => ({ ...a, open: false }))}
        variant={alert.variant}
        title={alert.title}
        message={alert.message}
        primaryAction={alert.primaryAction}
        secondaryAction={alert.secondaryAction}
        autoCloseMs={alert.autoCloseMs}
      />

      <Dialog
        open={open}
        onClose={setOpen}
        className="relative z-10"
        style={{ zIndex: 99 }}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary">
                  <CheckIcon aria-hidden="true" className="size-6 text-white" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle
                    as="h3"
                    className="text-base font-semibold text-gray-900"
                  >
                    Registration successful
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      We’re excited to have you as part of our team! Browse
                      packages, get assessments, and track everything in one
                      place.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigate("/dashboard/assessments/addAssessments");
                    }}
                    className={`w-full mt-4 px-4 py-2 text-sm font-semibold text-white bg-primary`}
                  >
                    Add Assessments
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full"
      >
        <main className="relative w-full overflow-hidden">
          <div className="relative z-10 mx-auto w-full">
            {/* Frosted panel */}
            <section
              className="
            mt-5 sm:mt-6 lg:mt-8
             border border-white/15 bg-white/5 bg-[#11406c]/40
            backdrop-blur-xs shadow-[0_10px_40px_rgba(0,0,0,0.35)]
            ring-1 ring-white/5
          "
            >
              <div>
                {/* Heading */}

                <h2 className="text-center font-semibold text-xl sm:text-2xl lg:text-3xl bg-black p-4">
                  Register
                </h2>

                {/* Cards grid */}
                <div className="p-4 sm:p-6 lg:p-8 bg-[#000054] grid gap-4 sm:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2">
                  {/* Pro */}
                  <div className="flex flex-col justify-between items-center text-center border-2 p-3 sm:p-4 bg-white/5 h-full max-h-[80vh] overflow-auto">
                    <div className="flex flex-col items-center w-full">
                      <p className="w-full py-1 bg-white text-black text-sm sm:text-base font-bold mb-3 ">
                        Choose a plan : {option}
                      </p>

                      <div
                        onClick={() => {
                          setoption("Core");
                        }}
                        className={`w-full mb-4 ${
                          option == "Core"
                            ? "bg-primary text-white"
                            : "bg-white text-black"
                        } flex direction-row justify-between items-center p-4 hover:bg-primary hover:text-white cursor-pointer text-black`}
                      >
                        <img
                          src="https://res.cloudinary.com/dcfotquxt/image/upload/v1758024662/Ai-AssEss/Screenshot_2025-09-16_at_8.05.31_AM_ikbwup.png"
                          className="w-28"
                          alt="BizVantage Core"
                        />
                        <p className="text-sm font-bold">$500 Monthly</p>
                      </div>
                      <div
                        onClick={() => {
                          setoption("Pro");
                        }}
                        className={`w-full mb-4 ${
                          option == "Pro"
                            ? "bg-primary text-white"
                            : "bg-white text-black"
                        } flex direction-row justify-between items-center p-4 hover:bg-primary hover:text-white cursor-pointer text-black`}
                      >
                        <img
                          src="https://res.cloudinary.com/dcfotquxt/image/upload/v1758024592/Ai-AssEss/Screenshot_2025-09-16_at_8.05.42_AM_wdzji9.png"
                          className="w-28"
                          alt="BizVantage Core"
                        />
                        <p className="text-sm font-bold">$1500 Monthly</p>
                      </div>
                      <div
                        onClick={() => {
                          setoption("Enterprise");
                        }}
                        className={`w-full mb-4 ${
                          option == "Enterprise"
                            ? "bg-primary text-white"
                            : "bg-white text-black"
                        } flex direction-row justify-between items-center p-4 hover:bg-primary hover:text-white cursor-pointer text-black`}
                      >
                        <img
                          src="https://res.cloudinary.com/dcfotquxt/image/upload/v1758024625/Ai-AssEss/Screenshot_2025-09-16_at_8.05.47_AM_cpzjwm.png"
                          className="w-28"
                          alt="BizVantage Core"
                        />
                        <p className="text-sm font-bold">Submit Info Below</p>
                      </div>

                      {/* Form */}
                      {/* {option == "Enterprise" ? (
                        <div className="w-full mb-4 text-left bg-[#335070] p-2 sm:p-3 ">
                          <FormField id="name" label="Name:" type="text" />
                          <FormField id="email" label="Email:" type="email" />
                          <FormField
                            id="phone"
                            label="Phone Number:"
                            type="tel"
                          />
                        </div>
                      ) : null} */}
                    </div>

                    {/* <div className="w-full">
                      <button
                        type="button"
                        className="
                        w-full justify-center bg-white px-5 py-2
                        text-sm sm:text-base font-semibold text-black
                        shadow-sm border-2 border-primary1
                        hover:bg-[#285172] hover:text-white
                      
                      "
                      >
                        Submit
                      </button>
                    </div> */}
                  </div>
                  {/* Core */}
                  <div className="flex flex-col justify-between items-center text-center border-2 p-3 sm:p-4 bg-white/5 h-full">
                    <div className="flex flex-col items-center w-full">
                      <p className="w-full py-1 bg-white text-black text-sm sm:text-base font-bold mb-3">
                        Registration
                      </p>
                      <div className="flex flex-col space-y-1 mt-2 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Full
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          autoComplete="name"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Phone Number
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          autoComplete="tel"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Email
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 w-full text-left mb-2">
                        <label className="text-sm font-medium text-white">
                          Password
                        </label>
                        <input
                          type="password"
                          className="border border-primary1 text-primary1 px-1 py-1"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 w-full text-left">
                        <label className="text-sm font-medium text-white">
                          Confirm Password
                        </label>
                        <input
                          type="password"
                          className="border border-primary1 text-primary1 px-1 py-1"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>

                      {error && (
                        <p className="text-red-600 text-xs mt-2">{error}</p>
                      )}
                    </div>

                    <div className="w-full">
                      <button
                        type="button"
                        className="
                        w-full justify-center bg-white px-5 py-2
                        text-sm sm:text-base font-semibold text-black
                        shadow-sm border-2 border-primary1
                        hover:bg-[#285172] hover:text-white
                        
                      "
                        onClick={handleRegister}
                      >
                        Register
                      </button>
                    </div>
                  </div>

                  {/* Enterprise */}
                  {/* <div className="flex flex-col justify-between items-center text-center border-2 p-3 sm:p-4 bg-white/5 h-full">
                    <div className="flex flex-col items-center w-full">
                      <p className="w-full py-1 bg-white text-black text-sm sm:text-base font-bold mb-3 flex text-center justify-center items-center ">
                        Payment
                        <img
                          src="https://www.astroshop.eu/CMS/images/text/category/Kreditkarten_Service_all.jpg"
                          className="w-28"
                          alt="Payment image"
                        />
                      </p>
                      <p className="w-full py-1 bg-white text-black text-xs font-bold mb-1 flex-col text-center justify-center items-center ">
                        You have selected
                        <img
                          src={`${
                            option == "Core"
                              ? "https://res.cloudinary.com/dcfotquxt/image/upload/v1758024662/Ai-AssEss/Screenshot_2025-09-16_at_8.05.31_AM_ikbwup.png"
                              : option == "Pro"
                              ? "https://res.cloudinary.com/dcfotquxt/image/upload/v1758024592/Ai-AssEss/Screenshot_2025-09-16_at_8.05.42_AM_wdzji9.png"
                              : "https://res.cloudinary.com/dcfotquxt/image/upload/v1758024625/Ai-AssEss/Screenshot_2025-09-16_at_8.05.47_AM_cpzjwm.png"
                          }`}
                          className="w-40 mx-auto"
                          alt="Payment image"
                        />
                      </p>

                      <div className="flex flex-col space-y-1 mt-2 w-full text-left">
                        <label className="text-sm font-medium text-white">
                          Card
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          autoComplete="name"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 mt-2 w-full text-left">
                        <label className="text-sm font-medium text-white">
                          Expiration
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          autoComplete="name"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 mt-2 w-full text-left">
                        <label className="text-sm font-medium text-white">
                          CVV
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          autoComplete="name"
                        />
                      </div>
                      <div className="flex flex-col space-y-1 mt-2 w-full text-left">
                        <label className="text-sm font-medium text-white">
                          Card Holder Name
                        </label>
                        <input
                          className="border border-primary1 text-primary1 px-1 py-1"
                          autoComplete="name"
                        />
                      </div>
                    </div>

                    <div className="w-full">
                      <button
                        type="button"
                        className="
                        w-full justify-center bg-white px-5 py-2
                        text-sm sm:text-base font-semibold text-black
                        shadow-sm border-2 border-primary1
                        hover:bg-[#285172] hover:text-white
                        
                      "
                      >
                        {option == "Core"
                          ? "Pay $500.00"
                          : option == "Pro"
                          ? "Pay $1500.00"
                          : "Contact Us"}
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>
            </section>
          </div>
        </main>
      </motion.div>
    </div>
  );
}

/* ----------------- small helpers ----------------- */

function FormField({ id, label, type = "text" }) {
  return (
    <div className="mb-2">
      <label
        htmlFor={id}
        className="block text-xs sm:text-sm font-medium text-white"
      >
        {label}
      </label>
      <div className="border-0 border-b-2 border-white/40 focus-within:border-white mb-2">
        <input
          id={id}
          name={id}
          type={type}
          className="
            block w-full border-0 bg-transparent
            px-2 py-1 text-sm sm:text-base text-white
            placeholder:text-white/60
            focus:outline-none
          "
        />
      </div>
    </div>
  );
}
